import { useState } from "react";

import { faImage } from "@fortawesome/pro-regular-svg-icons";
import { faImage as faImageSolid } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled, { useTheme } from "styled-components";

import type { Therapist } from "api/models/Therapist";
import { useProfileContext } from "contexts/ProfileContext";
import { updateImage } from "routes/settings/queries";
import { Avatar } from "shared/atoms/Avatar";
import { BaseButton } from "shared/atoms/BaseButton";
import { styles } from "shared/atoms/BaseButton/BaseButton";
import { Notification } from "shared/atoms/Notification";
import { BrazeService } from "utils/braze/BrazeService";
import { useWindowSize } from "utils/hooks";

import { useOnboardingNavigation } from "..";

import { IconContainer } from "./styles";

export const ProfilePicture = (): JSX.Element => {
  const { profile, setProfile } = useProfileContext();
  const [avatar, setAvatar] = useState(profile?.avatars?.medium || "");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState("");
  const theme = useTheme();
  const { width } = useWindowSize();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { nextRoute } = useOnboardingNavigation();

  const sendImage = (image: File | undefined) => {
    if (image) {
      setUploadProgress(0);
      const imgData = new FormData();
      imgData.set("file", image);
      updateImage(imgData, event => {
        setUploadProgress(Math.round((100 * event.loaded) / (event.total ?? event.loaded)));
      })
        .then(({ data }) => {
          const updatedProfile = {
            ...profile,
            avatars: {
              large: data?.avatar?.large_avatar?.url || null,
              medium: data?.avatar?.medium_avatar?.url || null,
              small: data?.avatar?.small_avatar?.url || null,
            },
          };
          setProfile(updatedProfile as Therapist);
          const resultSrc = data?.avatar?.medium_avatar?.url || "";
          setAvatar(resultSrc);
          if (resultSrc) {
            BrazeService.setAvatarImageUrl(resultSrc);
          }
          setUploadError("");
        })
        .catch(() => {
          setUploadError(t("settings.upload_failed"));
        })
        .finally(() => {
          setUploadProgress(0);
        });
    }
  };

  const aboveBreakpoint = width > parseInt(theme.breakpoint, 10);

  const radius = aboveBreakpoint ? 38 : 23;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (uploadProgress / 100) * circumference;
  const size = aboveBreakpoint ? 80 : 50;

  return (
    <>
      <Stack sx={{ alignItems: "center" }}>
        <IconContainer>
          <FontAwesomeIcon icon={faImageSolid} fontSize="32px" />
        </IconContainer>

        <Typography variant="h2" mt="16px" mb="8px">
          {t("onboarding.2.title")}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {t("onboarding.2.body")}
        </Typography>
      </Stack>

      <Stack sx={{ alignItems: "center" }}>
        <ProfilePictureUpload>
          <ProfilePictureUploadContainer>
            <AvatarContainer $visible={uploadProgress === 0}>
              <Avatar src={avatar} size={size} />
            </AvatarContainer>

            <ProgressRing $visible={uploadProgress > 0}>
              <circle stroke="rgb(218, 236, 255)" strokeWidth="4" fill="transparent" />
              <circle
                stroke="rgb(74, 144, 226)"
                strokeWidth="4"
                strokeDasharray={`${circumference} ${circumference}`}
                strokeDashoffset={offset}
                fill="transparent"
              />
            </ProgressRing>
          </ProfilePictureUploadContainer>

          <StyledInput
            data-testid="onboarding-profile-picture-file-input"
            id="profilePicture"
            type="file"
            accept="image/*"
            onChange={event => {
              if (event?.target?.files?.[0]) {
                sendImage(event.target.files?.[0]);
              }
            }}
          />
          <StyledLabel data-testid="onboarding-profile-picture-file-input-label" htmlFor="profilePicture">
            <FontAwesomeIcon icon={faImage} />
            <span>{avatar ? t("onboarding.2.upload_new") : t("onboarding.2.upload")}</span>
          </StyledLabel>
        </ProfilePictureUpload>

        {uploadError && (
          <Notification variant="danger" style={{ margin: "16px 0 20px 0" }}>
            {uploadError}
          </Notification>
        )}
      </Stack>

      <Stack spacing={2} width="100%">
        <BaseButton
          dataTestID="onboarding-profile-picture-next-btn"
          text={t("buttons.next")}
          onClick={() => {
            if (!avatar) {
              setUploadError(t("onboarding.2.upload_error"));
            } else if (nextRoute) {
              navigate(nextRoute);
            }
          }}
          icon="arrow-right"
          iconPlacement="end"
          fullWidth
        />

        <BaseButton
          dataTestID="onboarding-profile-picture-skip-btn"
          variant="secondaryLink"
          text={t("buttons.skip_for_now")}
          onClick={() => nextRoute && navigate(nextRoute)}
          fullWidth
        />
      </Stack>
    </>
  );
};

const ProfilePictureUpload = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ProfilePictureUploadContainer = styled.div`
  height: 80px;
  width: 80px;
  position: relative;

  ${props => props.theme.belowBreakpoint} {
    height: 50px;
    width: 50px;
  }
`;

const AvatarContainer = styled.div<{ $visible: boolean }>`
  transition: opacity 200ms;
  opacity: ${props => (props.$visible ? "1" : "0")};
`;

const ProgressRing = styled.svg<{ $visible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  height: 80px;
  width: 80px;
  transition: opacity 200ms;
  opacity: ${props => (props.$visible ? "1" : "0")};

  circle {
    transition: stroke-dashoffset 0.35s;
    transform: rotate(-90deg);
    transform-origin: 50% 50%;
    r: 38;
    cx: 40;
    cy: 40;
  }

  ${props => props.theme.belowBreakpoint} {
    height: 50px;
    width: 50px;

    circle {
      r: 23;
      cx: 25;
      cy: 25;
    }
  }
`;

const StyledInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const StyledLabel = styled.label`
  ${styles.action}
  cursor: pointer;
  display: flex;
  gap: 8px;
`;
